/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { PlayerBuilder } from '../../PlayerBuilder';
import {
  Action,
  AdBreak,
  AdBreakInfo,
  AdEvent,
  AdInfo,
  AdPlaybackPolicy,
  ImaSsaiPlaybackPolicyHandler,
  AdPlayer,
  AdTrackingEvent,
  PLAYBACK_STATES,
  PlaybackAutoSeekMode,
  PlaybackFastForwardMode,
  PlaybackInterruptedMode,
  PlaybackOptions,
  PlaybackRepeatedMode,
  PlaybackRewindMode,
  Player,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  TrackInfo,
  TrackVariantInfo,
  TrackType,
  AdTechnology,
} from '@quickplay/player-interface';
import { createEventEmitter, Error, EventEmitter, Logger } from '@quickplay/foundation';
import {
  createAdBreakInfo,
  createAdPlaybackFailure,
  createSsaiAdBreaks,
  createSsaiAdInfo,
  toAd,
  toAdBreak,
  updateAdBreaks,
} from '../util/AdUtil';
import { SsaiAdPlaybackPolicyHandler } from './policy/SsaiAdPlaybackPolicyHandler';

const defaultimaDaiAdsPlaybackPolicy: AdPlaybackPolicy = {
  fastForwardMode: PlaybackFastForwardMode.TRICK_FW_PLAY_LAST,
  rewindMode: PlaybackRewindMode.TRICK_RW_SKIP_ALL,
  autoSeekMode: PlaybackAutoSeekMode.AUTO_SEEK_PLAY_LAST,
  repeatMode: PlaybackRepeatedMode.REPEAT_PLAY_ONCE,
  interruptMode: PlaybackInterruptedMode.INTERRUPT_RESUME,
};

export function createIMASSAIAdsPlayer(builder: PlayerBuilder): AdPlayer {
  const logger: Logger = builder.configuredLogger;
  const eventEmitter: EventEmitter = createEventEmitter();
  const imaSDK: any = builder.imaSDK;
  let streamManager: any = builder.imaDaiStreamManager?.streamManager;
  const cuepoints = builder.imaDaiStreamManager?.cuepoints();
  const contentPlayer: Player = builder.player;
  const isLive = builder.imaDaiStreamManager.isLive();
  const adPlaybackPolicy: AdPlaybackPolicy = builder.adPlaybackPolicy || defaultimaDaiAdsPlaybackPolicy;
  const adContainerElement: Element = builder.imaDaiStreamManager.getAdContainerElement();

  // let currentTime = 0;
  // let duration = 0;
  let playbackPolicyHandler: ImaSsaiPlaybackPolicyHandler | undefined;

  let adBreaks: AdBreak[] | undefined = undefined;
  let activeAdBreakInfo: AdBreakInfo | undefined = undefined;
  let activeAdInfo: AdInfo | undefined = undefined;
  let isInAdBreak = false;

  const adEvents = [
    imaSDK.dai.api.StreamEvent.Type.LOADED,
    imaSDK.dai.api.StreamEvent.Type.ERROR,
    imaSDK.dai.api.StreamEvent.Type.AD_BREAK_STARTED,
    imaSDK.dai.api.StreamEvent.Type.AD_BREAK_ENDED,
    imaSDK.dai.api.StreamEvent.Type.AD_PROGRESS,
    imaSDK.dai.api.StreamEvent.Type.AD_PERIOD_STARTED,
    imaSDK.dai.api.StreamEvent.Type.AD_PERIOD_ENDED,
    imaSDK.dai.api.StreamEvent.Type.CUEPOINTS_CHANGED,
    imaSDK.dai.api.StreamEvent.Type.CLICK,
    imaSDK.dai.api.StreamEvent.Type.STARTED,
    imaSDK.dai.api.StreamEvent.Type.FIRST_QUARTILE,
    imaSDK.dai.api.StreamEvent.Type.MIDPOINT,
    imaSDK.dai.api.StreamEvent.Type.STREAM_INITIALIZED,
    imaSDK.dai.api.StreamEvent.Type.THIRD_QUARTILE,
    imaSDK.dai.api.StreamEvent.Type.COMPLETE,
    imaSDK.dai.api.StreamEvent.Type.SKIPPABLE_STATE_CHANGED,
    imaSDK.dai.api.StreamEvent.Type.SKIPPED,
    imaSDK.dai.api.StreamEvent.Type.VIDEO_CLICKED,
    imaSDK.dai.api.StreamEvent.Type.PAUSED,
    imaSDK.dai.api.StreamEvent.Type.RESUMED,
  ];

  const imaDaiPlayer: AdPlayer = {
    subscribe: eventEmitter.subscribe,
    unsubscribe: eventEmitter.unsubscribe,

    get playerState() {
      return contentPlayer.playerState;
    },

    get isBuffering() {
      return contentPlayer.isBuffering;
    },

    get isSeeking() {
      return contentPlayer.isSeeking;
    },

    get currentTime() {
      return streamManager?.contentTimeForStreamTime(contentPlayer.currentTime) ?? contentPlayer.currentTime;
    },

    get currentEpochTime() {
      return contentPlayer.isLive
        ? contentPlayer.currentEpochTime
        : (streamManager?.contentTimeForStreamTime(contentPlayer.currentTime) ?? contentPlayer.currentEpochTime);
    },

    get duration() {
      return playbackPolicyHandler ? playbackPolicyHandler.getContentDuration() : contentPlayer.duration;
    },

    get playbackRate() {
      return contentPlayer.playbackRate;
    },

    get availableBitrates() {
      return contentPlayer.availableBitrates;
    },

    get minBitrate() {
      return contentPlayer.minBitrate;
    },

    get maxBitrate() {
      return contentPlayer.maxBitrate;
    },

    get internalPlayerVersion() {
      return contentPlayer.internalPlayerVersion;
    },

    get internalPlayerName() {
      return 'IMA DAI SDK';
    },

    get isLive() {
      return isLive;
    },

    get remainingTime() {
      if (activeAdInfo) {
        return activeAdInfo.remainingTimeMs || 0;
      }
      return -1;
    },

    get isMute() {
      return contentPlayer.isMute;
    },

    get volume() {
      return contentPlayer.volume;
    },

    set volume(volumeLevel: number) {
      contentPlayer.volume = volumeLevel;
    },

    get adInsertionType(): AdTechnology {
      return AdTechnology.SSAI;
    },

    load(): void {
      contentPlayer.load();
    },

    play(): void {
      contentPlayer.play();
    },

    pause(): void {
      contentPlayer.pause();
    },

    abort: (error: Error) => {
      contentPlayer.abort(error);
    },

    send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
      logger.log(`streamTimelineEvent: ${eventName} Action: ${action} metadata: ${metadata}`);
    },

    seek(seekPoint: number): void {
      if (!playbackPolicyHandler) {
        contentPlayer.seek(seekPoint);
      } else {
        const willSeek = playbackPolicyHandler.willSeekTo(streamManager?.streamTimeForContentTime(seekPoint));
        contentPlayer.seek(willSeek);
      }
    },

    async getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
      return contentPlayer.getThumbnail(position);
    },

    getAllTracks(): TrackInfo[] {
      return [];
    },

    getActiveTracks(): TrackInfo[] {
      return [];
    },

    getVariantTracks(): TrackVariantInfo[] {
      return [];
    },

    getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
      return contentPlayer.getAvailableTrackVariants(trackType);
    },

    seekableRange(): SeekRangeInfo {
      if (contentPlayer.isLive) {
        return contentPlayer.seekableRange();
      }

      return {
        start: 0,
        end: this.duration,
      };
    },

    seekToLiveEdge(): void {
      // no-op
    },

    currentOffsetFromLiveEdge(): number {
      return 0;
    },

    selectTrack(_track?: TrackInfo): void {
      // no-op
    },

    stop: (): Promise<void> => {
      return contentPlayer.stop();
    },

    muteOrUnmute(): void {
      // no-op
    },

    skipAd(): void {
      if (!activeAdInfo) {
        return;
      }

      playbackPolicyHandler?.skipAd(toAd(activeAdInfo), undefined);

      activeAdInfo = undefined;
      activeAdBreakInfo = undefined;
    },

    resize(_width: number, _height: number, _isFullscreen: boolean): void {
      // TODO: implement
    },

    discardAdBreak(): void {
      // TODO: implement
    },

    getCuePoints(): number[] {
      if (!cuepoints?.length) {
        return [];
      }

      return playbackPolicyHandler
        ? cuepoints.map((adBreak: any) => streamManager?.contentTimeForStreamTime(adBreak?.start))
        : cuepoints.map((adBreak: any) => adBreak?.start);
    },

    setRequestInterceptor(_requestInterceptor: (requestType: RequestType, request: any) => void): void {
      // no-op
    },
    setResponseInterceptor(_responseInterceptor: (requestType: RequestType, response: any) => void): void {
      // no-op
    },

    restrictQuality(_enableRestriction: boolean): void {
      // no-op
    },

    accessFunctionHandler(_json: string): void {
      // no-op
    },

    availableMimeTypes(trackType: TrackType): Array<string> {
      return contentPlayer.availableMimeTypes(trackType);
    },

    setPreferredMimeTypes(_trackType: TrackType, _mimeTypes: Array<string>): void {
      contentPlayer.setPreferredMimeTypes(_trackType, _mimeTypes);
    },

    setPlaybackOptions(playbackOptions: PlaybackOptions): void {
      contentPlayer.setPlaybackOptions(playbackOptions);
    },

    unloadAndSavePreload(): Promise<void> {
      // no-op
      return Promise.resolve();
    },

    restoreFromPreload(): void {
      // no-op
    },
  };

  function onAdTrackingEvent(adTrackingEvent: AdTrackingEvent): any {
    // logger.log(`[DAI] ad tracking event ${adTrackingEvent}`);
    eventEmitter.publish(AdEvent.ADVERT_TRACKING_EVENT, adTrackingEvent);
  }

  /**
   *
   * @param event the event fired by the stream manager. Can be any of the following:
   * - ERROR: Fired when an error occurs.
   * - AD_BREAK_STARTED: Fired first time each ad break begins playback. If an ad break is watched subsequent times this will not be fired. Applications must disable seeking when this occurs.
   * - AD_BREAK_ENDED: Fired the first time each ad break ends. Applications must reenable seeking when this occurs.
   * - AD_PROGRESS: Fired when there is an update to an ad's progress.
   * - AD_PERIOD_STARTED: Fired every time the stream switches from content to advertising or slate. This will be fired even when an ad is played a second time or when seeking into an ad.
   * - AD_PERIOD_ENDED: Fired every time the stream switches from advertising or slate to content. This will be fired even when an ad is played a second time or when seeking into an ad.
   * - CUEPOINTS_CHANGED: Dispatched for on-demand streams when the cuepoints change.
   * - CLICK: Dispatched when the click element is clicked or tapped while an ad is being played.
   * - STARTED: Fired when an ad starts.
   * - FIRST_QUARTILE: Fired when an ad reaches its first quartile.
   * - MIDPOINT: Fired when an ad reaches its midpoint.
   * - STREAM_INITIALIZED: Fired when the stream is initialized.
   * - THIRD_QUARTILE: Fired when an ad reaches its third quartile.
   * - COMPLETE: Fired when an ad is complete.
   * - SKIPPABLE_STATE_CHANGED: Fired when the displayed ad's skippable state is changed.
   * - SKIPPED: Fired when the ad is skipped by the user.
   * - VIDEO_CLICKED: Fired when a user clicks on the video without triggering clickthrough. When a "Learn More" button is shown, such as when on mobile web, the CLICK event is only fired when clicking the button. Other clicks will fire this event.
   * - PAUSED: Fired when the ad is paused by the user.
   * - RESUMED: Fired when the ad is resumed by the user.
   */
  function onStreamEvent(event: any): void {
    switch (event.type) {
      case imaSDK.dai.api.StreamEvent.Type.ERROR: {
        logger.error('[DAI] stream error', event);
        contentPlayer.abort(createAdPlaybackFailure(event));
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.AD_BREAK_STARTED: {
        logger.log('[DAI][STREAM_EVENT] AD_BREAK_STARTED');
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.LOADED: {
        logger.log('[DAI][STREAM_EVENT] LOADED');
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.AD_BREAK_ENDED: {
        logger.log('[DAI][STREAM_EVENT] AD_BREAK_ENDED');
        onAdBreakEnd();
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.AD_PROGRESS: {
        const adProgressData = event.getStreamData()?.adProgressData;
        const currentAdTime = adProgressData.currentTime;
        // currentTime = currentAdTime;

        if (activeAdInfo) {
          activeAdInfo = {
            ...activeAdInfo,
            adBreakInfo: activeAdBreakInfo,
            remainingTimeMs: (adProgressData.duration - adProgressData.currentTime) * 1000,
          };
        }

        eventEmitter.publish('adprogressupdate', activeAdInfo, currentAdTime);
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.AD_PERIOD_STARTED: {
        logger.log('[DAI][STREAM_EVENT] AD_PERIOD_STARTED', event);
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.AD_PERIOD_ENDED: {
        logger.log('[DAI][STREAM_EVENT] AD_PERIOD_ENDED', event);
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.CUEPOINTS_CHANGED: {
        logger.log('[DAI][CUEPOINTS] cuepointschanged', event.data, builder.videoElement.duration);
        if (!adBreaks) {
          adBreaks = createSsaiAdBreaks(event.data.cuepoints, builder.videoElement.duration);
          logger.log('[DAI][CUEPOINTS] adbreaks', adBreaks);
          playbackPolicyHandler?.setAdBreaks(adBreaks);
        }
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.CLICK: {
        onAdTrackingEvent(AdTrackingEvent.AD_CLICK);
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.STARTED: {
        logger.log('[DAI][STREAM_EVENT] STARTED');
        onAdStart(event);
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.FIRST_QUARTILE: {
        // logger.log('[DAI] ad first quartile', event);
        onAdTrackingEvent(AdTrackingEvent.AD_FIRST_QUARTILE_EVENT);
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.MIDPOINT: {
        // logger.log('[DAI] ad midpoint', event);
        onAdTrackingEvent(AdTrackingEvent.AD_MIDPOINT_EVENT);
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.STREAM_INITIALIZED: {
        logger.log('[DAI] stream initialized', event);
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.THIRD_QUARTILE: {
        // logger.log('[DAI] ad third quartile', event);
        onAdTrackingEvent(AdTrackingEvent.AD_THIRD_QUARTILE_EVENT);
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.COMPLETE: {
        logger.log('[DAI][STREAM_EVENT] COMPLETE');
        onAdEnd();
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.SKIPPABLE_STATE_CHANGED: {
        logger.log('[DAI] ad skippable state changed', event);
        onAdTrackingEvent(AdTrackingEvent.AD_SKIPPABLE_STATE_CHANGED);
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.SKIPPED: {
        logger.log('[DAI] skipped', event);
        onAdTrackingEvent(AdTrackingEvent.AD_SKIPPED_EVENT);
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.VIDEO_CLICKED: {
        onAdTrackingEvent(AdTrackingEvent.AD_VIDEO_CLICKED);
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.PAUSED: {
        break;
      }
      case imaSDK.dai.api.StreamEvent.Type.RESUMED: {
        // TODO: emit advert start
        onAdTrackingEvent(AdTrackingEvent.AD_RESUMED_EVENT);
        break;
      }
    }
  }

  function onAdBreakEnd(): void {
    eventEmitter.publish(AdEvent.ADVERT_BREAK_END, activeAdBreakInfo);
    logger.log('[DAI] adbreakend', activeAdBreakInfo);

    if (activeAdBreakInfo && adBreaks) {
      const adBreak = adBreaks.find(
        // eslint-disable-next-line no-shadow
        adBreak => adBreak.startTime === (activeAdBreakInfo ? activeAdBreakInfo?.adBreakStartTimeOffsetMs / 1000 : -1),
      );

      if (adBreak) {
        playbackPolicyHandler?.setWatchedAdBreak(adBreak);
        logger.log('[DAI] marked adbreak as watched', adBreak);
      }
    }

    activeAdInfo = undefined;
    activeAdBreakInfo = undefined;
    isInAdBreak = false;
    if (contentPlayer.isLive) {
      return;
    }

    const autoSeekPosition = playbackPolicyHandler?.getAutoSeekPositionIfAny();
    logger.log('[DAI] autoSeekPosition', autoSeekPosition);
    if (autoSeekPosition) {
      contentPlayer.seek(autoSeekPosition);
    } else if (playbackPolicyHandler) {
      contentPlayer.seek(playbackPolicyHandler.willSeekTo(contentPlayer.currentTime));
    }
  }

  function onAdStart(event: any): void {
    if (!isInAdBreak) {
      activeAdBreakInfo = createAdBreakInfo(event.getAd().getAdPodInfo());
      if (playbackPolicyHandler && !playbackPolicyHandler.playbackRepeatedRule(toAdBreak(activeAdBreakInfo))) {
        contentPlayer.seek((activeAdBreakInfo.adBreakStartTimeOffsetMs + activeAdBreakInfo.durationMs) / 1000);
        return;
      }

      isInAdBreak = true;
      eventEmitter.publish(AdEvent.ADVERT_BREAK_START, activeAdBreakInfo);
      logger.log('[DAI] adbreakstart', activeAdBreakInfo);

      const updatedAdBreaks = updateAdBreaks(adBreaks, activeAdBreakInfo);

      if (updatedAdBreaks) {
        adBreaks = updatedAdBreaks;
        playbackPolicyHandler?.setAdBreaks(adBreaks);
        logger.log('[DAI] adbreaks updated', adBreaks);
      }
    }

    activeAdInfo = createSsaiAdInfo(event, activeAdBreakInfo, {
      technology: 'server-side',
      stitcher: 'Google DAI',
      managerName: 'Google IMA SDK',
      managerVersion: imaSDK.VERSION,
    });
    // duration = activeAdInfo.durationMs || 0;
    eventEmitter.publish(AdEvent.ADVERT_START, activeAdInfo);
    logger.log('[DAI] adstart', activeAdInfo);
  }

  function onAdEnd(): void {
    if (activeAdInfo) {
      activeAdInfo = { ...activeAdInfo, remainingTimeMs: 0 };
    }
    eventEmitter.publish(AdEvent.ADVERT_END, activeAdInfo);
    logger.log('[DAI] adend', activeAdInfo);
    activeAdInfo = undefined;
  }

  function setupAdPlaybackPolicy(daiAdPlaybackPolicy?: AdPlaybackPolicy): void {
    if (isLive || !daiAdPlaybackPolicy) {
      return;
    }
    playbackPolicyHandler = new SsaiAdPlaybackPolicyHandler(contentPlayer, streamManager, daiAdPlaybackPolicy, logger);

    const playbackOptions = contentPlayer.playbackOptions;

    if (playbackOptions?.initialPlaybackTime) {
      const ipt = playbackPolicyHandler.willSeekTo(
        playbackPolicyHandler.getPlayheadForContentPosition(playbackOptions.initialPlaybackTime),
      );
      logger.log('[DAI] initial playback time', playbackOptions.initialPlaybackTime, ipt);
      contentPlayer.setPlaybackOptions({
        ...playbackOptions,
        initialPlaybackTime: ipt,
      });
    }
  }

  function setupIMADAI(daiStreamManager: any): void {
    logger.log('[DAI] initializing IMA DAI SDK');

    daiStreamManager.addEventListener(adEvents, onStreamEvent, false);
  }

  function onLoaded(): void {
    /**
     * Monitors VOD content playback to manage ad break skipping based on playback policy.
     * This method is specifically designed for VOD content where the
     * Ad playback policy is set to REPEAT_PLAY_ONCE.
     */
    if (!contentPlayer.isLive && adPlaybackPolicy?.repeatMode === PlaybackRepeatedMode.REPEAT_PLAY_ONCE) {
      contentPlayer.subscribe('progressupdate', onVodProgressUpdate);
    }

    adBreaks = createSsaiAdBreaks(cuepoints, contentPlayer.duration);
    logger.log('[DAI]content loaded with no preroll', adBreaks);
    playbackPolicyHandler?.setAdBreaks(adBreaks);
  }

  function onTimedMetadata(event: any): void {
    streamManager?.processMetadata('ID3', event.payload.data, event.startTime);
  }

  function onDaiTimelineRegionEnter(event: any): void {
    const detail = event.detail;
    /**
     * ID3 Metadata Handler - Shaka Player Version Compatibility
     *
     * Supports both legacy and current Shaka Player API versions:
     * - Legacy (< 4.12.4): detail.eventElement.getAttribute('messageData')
     * - Current (4.12.4+): detail.eventNode.getAttribute('messageData')
     *
     * @see https://developers.google.com/ad-manager/dynamic-ad-insertion/sdk/html5/timed-metadata#shaka_player
     */
    let mediaId: string | undefined;
    const pts = detail.startTime;

    // Try new API first (Shaka Player 4.12.4+)
    if (detail.eventNode?.attributes?.['messageData']) {
      mediaId = detail.eventNode.attributes['messageData'];
    }
    // Fallback to legacy API (Shaka Player < 4.12.4)
    else if (detail.eventElement?.getAttribute) {
      mediaId = detail.eventElement.getAttribute('messageData');
    }

    if (mediaId) {
      streamManager?.processMetadata('urn:google:dai:2018', mediaId, pts);
    }
  }

  function onPlaybackStateChanged(state: PLAYBACK_STATES): void {
    if (state === PLAYBACK_STATES.STOPPED || state === PLAYBACK_STATES.IDLE) {
      // Note:
      // The ad player explicitly emits the playback state IDLE or STOPPED because it unsubscribes from content player events during cleanup.
      // Since the content player reference is shared between the SSAI Ad composed player and ad player, this affects other subscribers
      // that rely on `playbackstatechanged` events to detect STOPPED or IDLE states.
      // This is a workaround to ensure the ad player reliably emits a playback state change when it transitions to STOPPED or IDLE.
      eventEmitter.publish('playbackstatechanged', state);
      cleanup();
    }
  }

  /**
   * Monitors VOD content playback to manage ad break skipping based on playback policy.
   * This method is specifically designed for VOD content where the
   * Ad playback policy is set to REPEAT_PLAY_ONCE.
   */
  function onVodProgressUpdate(): void {
    if (!contentPlayer) {
      return;
    }
    if (!isInAdBreak) {
      playbackPolicyHandler?.handleWatchedAdBreak(contentPlayer.currentTime);
    }
  }

  function subscribeToPlayerEvents(player: Player): void {
    player.subscribe('timedmetadata', onTimedMetadata);
    player.subscribe('daitimelineregionenter', onDaiTimelineRegionEnter);
    player.subscribe('contentloaded', onLoaded);
    player.subscribe('playbackstatechanged', onPlaybackStateChanged);
  }

  function unsubscribeToPlayerEvents(player: Player): void {
    player.unsubscribe('timedmetadata', onTimedMetadata);
    player.unsubscribe('daitimelineregionenter', onDaiTimelineRegionEnter);
    player.unsubscribe('contentloaded', onLoaded);
    player.unsubscribe('playbackstatechanged', onPlaybackStateChanged);
    player.unsubscribe('progressupdate', onVodProgressUpdate);
  }
  function cleanup(): void {
    streamManager?.removeEventListener(adEvents, onStreamEvent, false);
    streamManager?.reset();
    streamManager = undefined;
    playbackPolicyHandler?.reset();
    playbackPolicyHandler = undefined;
    sanitizeAdElement(adContainerElement);
    removeAdIframes();
    if (contentPlayer) {
      unsubscribeToPlayerEvents(contentPlayer);
    }
  }

  function sanitizeAdElement(adElement: Element): void {
    if (!adElement) return;
    Array.from(adElement.childNodes).forEach(node => {
      if (node instanceof HTMLIFrameElement) {
        node.src = 'about:blank';
        try {
          node.contentWindow?.location.replace('about:blank');
        } catch {
          logger.log('[DAI] Cross-origin iframe cleanup blocked');
        }
      }
      adElement.removeChild(node);
    });

    logger.log('[DAI] Ad element sanitization complete');
  }

  function removeAdIframes(): void {
    const adIframes = document.querySelectorAll('iframe[title="Advertisement"]');
    adIframes.forEach(iframe => iframe.remove());
  }

  subscribeToPlayerEvents(contentPlayer);
  setupAdPlaybackPolicy(adPlaybackPolicy);
  setupIMADAI(streamManager);

  return imaDaiPlayer;
}
